import { FC } from "react";

import {
  AlertDialog,
  AlertDialogBackdrop,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  Button,
  ButtonText,
  Heading,
  HStack,
  Text,
  VStack,
} from "@/components/ui";
import { useLocalization } from "@/hooks";

interface DeleteDialogProps {
  body: string;
  deleteButtonText?: string;
  error?: string | null;
  isOpen: boolean;
  loading?: boolean;
  loadingText?: string;
  onCancel: () => void;
  onDelete: () => Promise<void>;
  title: string;
}

const DeleteDialog: FC<DeleteDialogProps> = ({
  body,
  deleteButtonText,
  error,
  isOpen,
  loading = false,
  loadingText,
  onCancel,
  onDelete,
  title,
}) => {
  const { i18n } = useLocalization();

  return (
    <AlertDialog isOpen={isOpen} onClose={onCancel}>
      <AlertDialogBackdrop />
      <AlertDialogContent className="gap-6">
        <AlertDialogHeader>
          <Heading size="lg">{title}</Heading>
        </AlertDialogHeader>
        <AlertDialogBody>
          <Text>{body}</Text>
        </AlertDialogBody>
        <AlertDialogFooter>
          <VStack className="gap-2">
            <HStack className="gap-3 justify-end">
              <Button
                className="rounded-xl"
                isDisabled={loading}
                onPress={onCancel}
                variant="outline"
              >
                <ButtonText>{i18n.t("misc.cancel")}</ButtonText>
              </Button>
              <Button
                action="negative"
                className="rounded-xl"
                isDisabled={loading}
                onPress={onDelete}
              >
                <ButtonText>
                  {loading
                    ? loadingText || i18n.t("misc.deleting")
                    : deleteButtonText || i18n.t("misc.delete")}
                </ButtonText>
              </Button>
            </HStack>
            {error && (
              <Text className="text-error-600 text-sm text-right">{error}</Text>
            )}
          </VStack>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default DeleteDialog;
